<template>
    <QueryLoaderGuard
        :is-pending="isPending"
        :is-error="isError"
    >
        <div class="plant-status-summary-chart__content">
            <template v-if="data">
                <v-chart
                    class="plant-status-summary-chart__content__chart"
                    :option="option"
                    autoresize
                />
            </template>
        </div>
    </QueryLoaderGuard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { use } from 'echarts/core'
import { EChartsOption } from 'echarts'
import { BarChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'
import {
    TooltipComponent,
    LegendComponent,
    GridComponent,
    DatasetComponent,
    MarkLineComponent,
    MarkPointComponent,
    ToolboxComponent,
    AriaComponent,
    TitleComponent,
} from 'echarts/components'
import VChart from 'vue-echarts'
import QueryLoaderGuard from '@/components/guards/QueryLoaderGuard.vue'
import { ConditionStatus, SUMMARY_STATUS } from '@/configuration/status/condition-status'
import { usePortfolioPlantStatusSummaryQuery } from '@/api/queries/transformers/portfolio-overview/usePortfolioPlantStatusSummaryQuery'
import { router } from '@/router'
import { CHART_ICON } from '@/utils/chart-utils'
import { ColorType } from '@/configuration/color/color-type'
import { useTheme } from '@/composables/useTheme'
import {
    getChartLabelBackgroundColor,
    getChartLabelColor,
    getChartToolboxInactiveColor,
} from '@/utils/echarts-theme'

use([
    TitleComponent,
    BarChart,
    CanvasRenderer,
    TooltipComponent,
    LegendComponent,
    GridComponent,
    DatasetComponent,
    MarkLineComponent,
    MarkPointComponent,
    ToolboxComponent,
    AriaComponent,
])

const props = defineProps<{
    plantGroupSid: number
}>()

const plantGroupSid = computed(() => {
    return props.plantGroupSid
})

const { t } = useI18n()
const { data, isPending, isError } = usePortfolioPlantStatusSummaryQuery(plantGroupSid)
const { isDarkMode } = useTheme()

const series = computed(() => [
    {
        ...SUMMARY_STATUS[ConditionStatus.Normal],
        data: data.value?.numberOfNormalComponents?.map((e: number) => e || '-') ?? [],
    },
    {
        ...SUMMARY_STATUS[ConditionStatus.Inferior],
        data: data.value?.numberOfInferiorComponents?.map((e: number) => e || '-') ?? [],
    },
    {
        ...SUMMARY_STATUS[ConditionStatus.Critical],
        data: data.value?.numberOfCriticalComponents?.map((e: number) => e || '-') ?? [],
    },
    {
        ...SUMMARY_STATUS[ConditionStatus.NoAction],
        data: data.value?.numberOfNoActionComponents?.map((e: number) => e || '-') ?? [],
    },
    {
        ...SUMMARY_STATUS[ConditionStatus.None],
        data: data.value?.numberOfNotAssessedComponents?.map((e: number) => e || '-') ?? [],
    },
])

const option = computed((): EChartsOption => {
    const options: EChartsOption = {
        title: {
            show: false,
            text: t('transformers.portfolioOverview.plantStatusSummary.headline'),
        },
        toolbox: {
            show: true,
            itemSize: 16,
            itemGap: 14,
            showTitle: true,
            feature: {
                saveAsImage: {
                    icon: CHART_ICON.SAVE,
                    title: t('chart.action.saveAsImage'),
                },
            },
            iconStyle: {
                color: getChartToolboxInactiveColor(isDarkMode.value),
                borderColor: 'none',
            },
            emphasis: {
                iconStyle: {
                    color: ColorType.Primary,
                    borderColor: 'none',
                },
            },
        },
        tooltip: {
            trigger: 'axis',
            enterable: true,
            confine: true,
            position: (point) => [point[0], point[1]],
            axisPointer: {
                type: 'shadow',
            },
            formatter: (args) => {
                if (!Array.isArray(args)) {
                    return ''
                }

                const mainContainer = document.createElement('div')
                mainContainer.classList.add('container')

                const nameDiv = document.createElement('div')
                nameDiv.classList.add('container__name')
                nameDiv.textContent = args[0].name
                mainContainer.appendChild(nameDiv)

                const contentDiv = document.createElement('div')
                contentDiv.classList.add('container__content')

                args.forEach((element) => {
                    const tooltipData = data.value?.tooltipData?.[element.dataIndex][element.seriesId ?? -1] ?? '-'

                    const itemDiv = document.createElement('div')
                    itemDiv.classList.add('container__content__item-div')

                    const colorDiv = document.createElement('div')
                    colorDiv.classList.add('container__content__item-div__color-div')

                    const colorCircle = document.createElement('span')
                    colorCircle.classList.add('container__content__item-div__color-div__color-circle')
                    colorCircle.style.backgroundColor = element.color?.toString() ?? '#666'
                    colorDiv.appendChild(colorCircle)

                    const seriesNameSpan = document.createElement('span')
                    seriesNameSpan.classList.add('container__content__item-div__color-div__series-name')
                    seriesNameSpan.textContent = element.seriesName ?? ''
                    colorDiv.appendChild(seriesNameSpan)

                    itemDiv.appendChild(colorDiv)

                    const tooltipSpan = document.createElement('span')
                    tooltipSpan.classList.add('container__content__item-div__tooltip')
                    itemDiv.appendChild(tooltipSpan)

                    if (Array.isArray(tooltipData)) {
                        tooltipData.forEach((element, index) => {
                            if (index !== 0) {
                                const dividerSpan = document.createElement('span')
                                dividerSpan.textContent = ', '
                                tooltipSpan.appendChild(dividerSpan)
                            }

                            const linkSpan = document.createElement('span')
                            linkSpan.textContent = element.componentName ?? '-'
                            linkSpan.classList.add('container__content__item-div__tooltip__link')
                            linkSpan.addEventListener('click', () => onTransformerClick(element.componentSid ?? ''))
                            tooltipSpan.appendChild(linkSpan)
                        })
                    } else {
                        tooltipSpan.textContent = '-'
                    }

                    contentDiv.appendChild(itemDiv)
                })

                mainContainer.appendChild(contentDiv)

                return mainContainer
            },
        },
        legend: {},
        grid: {
            top: 100,
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true,
        },
        xAxis: {
            type: 'value',
        },
        yAxis: {
            type: 'category',
            data: data.value?.plants ?? [],
        },
        aria: {
            enabled: true,
            decal: {
                show: true,
            },
        },
        series: [],
    }

    series.value.forEach((series) => {
        if (!Array.isArray(options.series)) {
            return
        }

        options.series.push({
            id: series.id,
            name: t(series.name),
            type: 'bar',
            stack: 'total',
            color: series.color,
            barWidth: '90%',
            barMaxWidth: 60,
            itemStyle: {
                decal: series.pattern,
            },
            label: {
                show: true,
                color: getChartLabelColor(isDarkMode.value),
                fontWeight: 'bold',
                backgroundColor: getChartLabelBackgroundColor(isDarkMode.value),
            },
            data: series.data,
        })
    })

    return options
})

const onTransformerClick = (transformerSid: string) => {
    const route = router.resolve({
        name: 'Transformers_ConditionStatus',
        params: {
            transformerId: transformerSid,
        },
    })
    
    window.open(route.href, '_blank')
}
</script>

<style scoped lang="scss">
.plant-status-summary-chart {
    &__content {
        &__chart {
            width: 100%;
            height: 700px;
        }
    }
}
</style>

<style lang="scss">
.container {
    &__name {
        font-size: $default-font-size;
        color: $pbl-text;
        font-weight: 500;
    }

    &__content {
        margin: 10px 0 0;

        &__item-div {
            margin: 10px 0 0;
            display: flex;
            gap: 16px;
            align-items: flex-start;

            &__color-div {
                display: flex;
                gap: 10px;
                align-items: center;

                &__color-circle {
                    display: inline-block;
                    border-radius: 10px;
                    width: 10px;
                    height: 10px;
                }

                &__series-name {
                    font-size: $default-font-size;
                    color: $pbl-text-muted;
                    font-weight: 400;
                    line-height: 18px;
                }
            }

            &__tooltip {
                font-size: $default-font-size;
                color: $pbl-text;
                font-weight: 500;
                line-height: 18px;
                max-width: 200px;
                white-space: normal;

                &__link {
                    color: $pbl-primary;
                    cursor: pointer;
                }
            }
        }
    }
}
</style>
